/**
 * L2 Champagne theme behaviours. Plain custom elements, no dependencies.
 *  - <l2-rail>          horizontal scroll rail with arrows and/or dots
 *  - <l2-tabs>          pill tabs that switch rail panels (product rail)
 *  - <quantity-stepper> in-card add to cart / change quantity via the Ajax Cart API
 */

const routes = (window.L2 && window.L2.routes) || {
  cart_add_url: '/cart/add',
  cart_change_url: '/cart/change',
  cart_url: '/cart',
  root_url: '/',
};

class L2Rail extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-rail-track]');
    if (!this.track) return;
    this.prev = this.querySelector('[data-rail-prev]');
    this.next = this.querySelector('[data-rail-next]');
    this.dotsHost = this.querySelector('[data-rail-dots]');
    this.items = Array.from(this.track.children);

    this.prev?.addEventListener('click', () => this.step(-1));
    this.next?.addEventListener('click', () => this.step(1));
    this.buildDots();

    this.onScroll = () => {
      window.cancelAnimationFrame(this.frame);
      this.frame = window.requestAnimationFrame(() => this.update());
    };
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.resizeObserver = new ResizeObserver(() => this.update());
    this.resizeObserver.observe(this.track);
    this.update();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.resizeObserver?.disconnect();
  }

  stepSize() {
    const first = this.items[0];
    if (!first) return this.track.clientWidth;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return first.getBoundingClientRect().width + gap;
  }

  step(direction) {
    this.track.scrollBy({ left: direction * this.stepSize(), behavior: 'smooth' });
  }

  buildDots() {
    if (!this.dotsHost || this.items.length < 2) return;
    this.dotsHost.innerHTML = '';
    this.dots = this.items.map((item, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'rail__dot';
      dot.setAttribute('aria-label', `${this.dataset.dotLabel || 'Item'} ${index + 1}`);
      dot.addEventListener('click', () => {
        this.track.scrollTo({ left: item.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
      });
      this.dotsHost.appendChild(dot);
      return dot;
    });
  }

  update() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    const left = this.track.scrollLeft;
    const overflowing = max > 1;
    this.toggleAttribute('data-overflowing', overflowing);
    if (this.prev) this.prev.disabled = !overflowing || left <= 1;
    if (this.next) this.next.disabled = !overflowing || left >= max - 1;

    if (this.dots) {
      let active = 0;
      let best = Infinity;
      this.items.forEach((item, index) => {
        const distance = Math.abs(item.offsetLeft - this.track.offsetLeft - left);
        if (distance < best) {
          best = distance;
          active = index;
        }
      });
      if (left >= max - 1) active = this.items.length - 1;
      this.dots.forEach((dot, index) => dot.setAttribute('aria-current', index === active ? 'true' : 'false'));
    }
  }
}

class L2Tabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.seeAll = this.querySelector('[data-see-all]');
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.select(index));
      tab.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        const nextIndex = (index + (event.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
        this.select(nextIndex);
        this.tabs[nextIndex].focus();
      });
    });
  }

  select(index) {
    this.tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.tabIndex = selected ? 0 : -1;
      const panel = this.querySelector(`#${tab.getAttribute('aria-controls')}`);
      if (panel) panel.hidden = !selected;
    });
    const url = this.tabs[index].dataset.url;
    if (this.seeAll && url) this.seeAll.href = url;
  }
}

class QuantityStepper extends HTMLElement {
  connectedCallback() {
    this.variantId = this.dataset.variantId;
    this.value = this.querySelector('.quantity-stepper__value');
    this.decrease = this.querySelector('[data-action="decrease"]');
    this.increase = this.querySelector('[data-action="increase"]');
    this.decrease?.addEventListener('click', () => this.change(-1));
    this.increase?.addEventListener('click', () => this.change(1));
    this.onCartUpdated = (event) => {
      const quantity = event.detail?.quantities?.[this.variantId];
      if (typeof quantity === 'number') this.render(quantity);
    };
    document.addEventListener('l2:cart-updated', this.onCartUpdated);
  }

  disconnectedCallback() {
    document.removeEventListener('l2:cart-updated', this.onCartUpdated);
  }

  get quantity() {
    return parseInt(this.dataset.quantity || '0', 10);
  }

  render(quantity) {
    this.dataset.quantity = String(quantity);
    if (this.value) this.value.textContent = String(quantity);
    if (this.decrease) this.decrease.disabled = quantity <= 0;
  }

  async change(delta) {
    if (this.getAttribute('aria-busy') === 'true') return;
    const current = this.quantity;
    const target = Math.max(0, current + delta);
    if (target === current) return;

    this.setAttribute('aria-busy', 'true');
    this.render(target);
    try {
      const isAdd = current === 0 && target > 0;
      const url = `${isAdd ? routes.cart_add_url : routes.cart_change_url}.js`;
      const body = isAdd
        ? { items: [{ id: Number(this.variantId), quantity: target }] }
        : { id: String(this.variantId), quantity: target };
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
      await publishCart();
      if (isAdd && !this.closest('cart-drawer')) openCart(true);
    } catch (error) {
      console.error(error);
      this.render(current);
    } finally {
      this.removeAttribute('aria-busy');
    }
  }
}

async function publishCart() {
  const response = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) return;
  const cart = await response.json();
  const quantities = {};
  cart.items.forEach((item) => {
    quantities[item.variant_id] = (quantities[item.variant_id] || 0) + item.quantity;
  });
  document.querySelectorAll('quantity-stepper').forEach((stepper) => {
    if (!(stepper.dataset.variantId in quantities)) quantities[stepper.dataset.variantId] = 0;
  });
  document.querySelectorAll('[data-cart-count]').forEach((badge) => {
    badge.textContent = String(cart.item_count);
    badge.hidden = cart.item_count === 0;
  });
  document.dispatchEvent(new CustomEvent('l2:cart-updated', { detail: { cart, quantities } }));
}

/* ---------- Cart drawer ---------- */
async function refreshCartDrawer() {
  const drawer = document.querySelector('cart-drawer');
  if (!drawer) return;
  const base = (routes.root_url || '/').replace(/\/$/, '');
  const response = await fetch(`${base}/?sections=cart-drawer`, { headers: { Accept: 'application/json' } });
  if (!response.ok) return;
  const data = await response.json();
  const doc = new DOMParser().parseFromString(data['cart-drawer'], 'text/html');
  const fresh = doc.querySelector('[data-cart-drawer-content]');
  const current = drawer.querySelector('[data-cart-drawer-content]');
  if (fresh && current) current.replaceWith(fresh);
}

async function openCart(added = false) {
  const drawer = document.querySelector('cart-drawer');
  if (!drawer) {
    window.location.href = routes.cart_url;
    return;
  }
  await refreshCartDrawer();
  const message = drawer.querySelector('[data-cart-added-message]');
  if (message) message.hidden = !added;
  const dialog = drawer.querySelector('dialog');
  if (dialog && !dialog.open) dialog.showModal();
}
window.L2 = Object.assign(window.L2 || {}, { openCart, refreshCartDrawer, publishCart });

class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => {
      const dialog = this.querySelector('dialog');
      if (event.target.closest('[data-cart-close]') || event.target === dialog) dialog.close();
    });
    this.onUpdated = () => {
      if (this.querySelector('dialog')?.open) refreshCartDrawer();
    };
    document.addEventListener('l2:cart-updated', this.onUpdated);
    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-cart-open]');
      if (!opener) return;
      event.preventDefault();
      openCart(false);
    });
  }
}

/* ---------- Product form (PDP) ---------- */
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.form.addEventListener('change', (event) => {
      if (event.target.name === 'id') this.onVariantChange(event.target);
    });
    this.form.addEventListener('submit', (event) => this.onSubmit(event));
  }

  onVariantChange(input) {
    this.querySelectorAll('[data-option-card]').forEach((card) => {
      card.toggleAttribute('data-selected', card.contains(input));
    });
    const url = new URL(window.location.href);
    url.searchParams.set('variant', input.value);
    window.history.replaceState({}, '', url);
    document.querySelectorAll('[data-sticky-price]').forEach((el) => {
      el.innerHTML = input.closest('[data-option-card]')?.querySelector('[data-option-price]')?.innerHTML || el.innerHTML;
    });
  }

  async onSubmit(event) {
    event.preventDefault();
    const button = this.form.querySelector('[type="submit"]');
    button?.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(`${routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(this.form),
      });
      if (!response.ok) throw new Error(`Add to cart failed: ${response.status}`);
      await publishCart();
      openCart(true);
    } catch (error) {
      console.error(error);
      this.form.submit();
    } finally {
      button?.removeAttribute('aria-busy');
    }
  }
}

/* ---------- Sticky add-to-cart bar (PDP) ---------- */
class StickyBuyBar extends HTMLElement {
  connectedCallback() {
    const target = document.querySelector(this.dataset.watch);
    if (!target) return;
    new IntersectionObserver(([entry]) => {
      this.toggleAttribute('data-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
    }).observe(target);
    this.querySelector('[data-sticky-add]')?.addEventListener('click', () => {
      document.querySelector(`${this.dataset.watch} [type="submit"]`)?.click();
    });
  }
}

/* ---------- Product recommendations (PDP) ---------- */
class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    new IntersectionObserver((entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.disconnect();
      fetch(this.dataset.url)
        .then((response) => response.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.querySelector(`product-recommendations[data-key="${this.dataset.key}"]`);
          if (fresh && fresh.innerHTML.trim()) this.innerHTML = fresh.innerHTML;
          else this.hidden = true;
        })
        .catch(() => { this.hidden = true; });
    }, { rootMargin: '0px 0px 400px 0px' }).observe(this);
  }
}

/* ---------- Collection facets ---------- */
class FacetsForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.panel = this.querySelector('#Facets');
    this.querySelectorAll('[data-facets-toggle]').forEach((button) => {
      button.addEventListener('click', () => {
        const open = !this.panel.hasAttribute('data-open');
        this.panel.toggleAttribute('data-open', open);
        this.querySelector('[aria-controls="Facets"]')?.setAttribute('aria-expanded', String(open));
      });
    });
    this.form.addEventListener('change', () => {
      if (this.panel?.hasAttribute('data-open') && !window.matchMedia('(min-width: 1024px)').matches) return;
      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(() => this.apply(), 300);
    });
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.apply();
    });
  }

  apply() {
    const params = new URLSearchParams(new FormData(this.form));
    for (const [key, value] of Array.from(params.entries())) {
      if (value === '') params.delete(key);
    }
    window.location.search = params.toString();
  }
}

if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);
if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);
if (!customElements.get('sticky-buy-bar')) customElements.define('sticky-buy-bar', StickyBuyBar);
if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);
if (!customElements.get('facets-form')) customElements.define('facets-form', FacetsForm);
if (!customElements.get('l2-rail')) customElements.define('l2-rail', L2Rail);
if (!customElements.get('l2-tabs')) customElements.define('l2-tabs', L2Tabs);
if (!customElements.get('quantity-stepper')) customElements.define('quantity-stepper', QuantityStepper);
